import type { Permission, PortalSettings, SessionResponse } from "@ai-platform/contracts";

/*
 * What the session allows, read from the session only. The portal never derives a permission
 * itself: `permissions` is the list the API computed with `can` (05 §6.2, ADR-075). These are
 * pure functions so the router guard, the navigation and the components agree.
 */

export type Session = SessionResponse | null;

/** Whether the session lists `action`; no session lists nothing. */
export function can(session: Session, action: Permission): boolean {
  return session?.permissions.includes(action) ?? false;
}

/**
 * Who may open a route.
 * - `public`: everyone, also without a session (the login, not found).
 * - `catalog`: the catalogue for prospects (07 §5): everyone while the guest view is on, every
 *   session once it is off (`GUEST_CATALOG_ENABLED`, ADR-133 point 3).
 * - `customer`: a session with a customer and the permission; staff without a membership have
 *   no customer pages.
 * - `staff`: a session with a staff role and the permission (05 §6.2, staff routes).
 */
export type RouteAccess =
  | { kind: "public" }
  | { kind: "catalog" }
  | { kind: "customer"; permission: Permission }
  | { kind: "staff"; permission: Permission };

/** `login` sends to the login with a return path; `not_found` hides what the actor may not see (K-05). */
export type AccessDecision = "allow" | "login" | "not_found";

/** Whether the catalogue is open: to every session, to guests while the guest view is on. */
const catalogueOpen = (session: Session, settings: PortalSettings) => session !== null || settings.guestCatalog;

export function decideAccess(access: RouteAccess, session: Session, settings: PortalSettings): AccessDecision {
  if (access.kind === "public") return "allow";
  if (access.kind === "catalog") return catalogueOpen(session, settings) ? "allow" : "login";
  if (session === null) return "login";
  const inArea = access.kind === "customer" ? session.customer !== null : session.staffRole !== null;
  return inArea && can(session, access.permission) ? "allow" : "not_found";
}

/**
 * Where `/` leads: the customer's overview, the staff area for staff only, else the catalogue,
 * for a guest without the guest view the login.
 */
export type HomeRoute = "home" | "staffCustomers" | "catalog" | "login";

export function homeFor(session: Session, settings: PortalSettings): HomeRoute {
  if (session?.customer && can(session, "customer.view")) return "home";
  if (session?.staffRole) return "staffCustomers";
  return catalogueOpen(session, settings) ? "catalog" : "login";
}
